/**
 * Shared copy explaining the window-relative stats (#1273, #1277): what the
 * live header label, the "New" badge, and the ▲/▼ window-over-window delta are
 * measured against. The live window is calendar-anchored *to date* — this ISO
 * week so far (weekly) / today (daily), UTC — and the delta compares it with
 * the same elapsed slice of the previous week/day (like-for-like). Used by the
 * page header, the legend, and the detail panel so the wording can't drift.
 */

import type { Granularity } from "../types";
import type { TrendDirection } from "./trend";

/** Short noun for the comparison window: "vs prev week" / "vs prev day". */
export function windowNoun(granularity: Granularity): "week" | "day" {
  return granularity === "weekly" ? "week" : "day";
}

/** The live period-to-date in prose: "this week" / "today". */
export function periodToDate(granularity: Granularity): "this week" | "today" {
  return granularity === "weekly" ? "this week" : "today";
}

/** Header label for the live view: "Week to date" / "Today". */
export function windowLabel(granularity: Granularity): "Week to date" | "Today" {
  return granularity === "weekly" ? "Week to date" : "Today";
}

/** Tooltip for the ▲/▼ delta. */
export function trendExplanation(granularity: Granularity): string {
  return `Change in this mode's failures ${periodToDate(granularity)}, versus the same point the previous ${windowNoun(granularity)}.`;
}

/** Tooltip for the "New" badge (live view only — #1273). */
export function newBadgeExplanation(granularity: Granularity): string {
  return `This failure mode first appeared ${periodToDate(granularity)}.`;
}

/**
 * Screen-reader label for the ▲/▼ delta (#1273). The visible glyph + "vs prev
 * week" is decorative; this spells out the full meaning so SR users get the
 * direction, the magnitude, *and* the comparison window — not just
 * "increasing", which an `aria-label` would otherwise collapse it to.
 */
export function trendAriaLabel(
  granularity: Granularity,
  direction: TrendDirection,
  delta: number,
  pct: number | null,
): string {
  const noun = windowNoun(granularity);
  if (direction === "flat") {
    return `No change versus the previous ${noun}.`;
  }
  const word = direction === "up" ? "Increased" : "Decreased";
  const pctText = pct === null ? "" : ` (${Math.abs(pct)}%)`;
  return `${word} by ${Math.abs(delta)}${pctText} versus the previous ${noun}.`;
}
